import { useState, useEffect } from "react";
import styles from "../Community/CreateCommunityPage.module.css";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { toast } from "react-toastify";
import PageSectionsManager from "../../components/PageSectionsManager";
import {
  usePageSections,
  sectionsFromApi,
  buildSectionsPayload,
} from "../../hooks/usePageSections";
import useDashboardStore from "../../store/dashboardStore";

const PAGE_TYPE = "replaysessions";

const EditReplaySessionsContentPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCommunityId, setSelectedCommunityId] = useState(null);
  const [academyTitle, setAcademyTitle] = useState("");
  const { communities } = useDashboardStore();

  const pageSections = usePageSections();

  const loadContent = async (communityId) => {
    try {
      const { data } = await api.get(
        ENDPOINTS.GET_PAGE_CONTENT(communityId, PAGE_TYPE),
      );
      setAcademyTitle(data?.content?.academy_title || "");
      pageSections.setSections(sectionsFromApi(data));
      pageSections.setActiveSectionIndex(0);
      pageSections.setVideoCoverFiles([]);
    } catch (err) {
      console.error("Failed to load replay sessions content", err);
      toast.error("Failed to load replay sessions content");
    }
  };

  useEffect(() => {
    if (!selectedCommunityId) return;
    loadContent(selectedCommunityId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommunityId]);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!selectedCommunityId) {
      toast.error("Please select a community first");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("page_type", PAGE_TYPE);
      formData.append("community_id", selectedCommunityId);
      if (academyTitle) formData.append("academy_title", academyTitle);

      const { sections, section_files, group_files, video_attachments } =
        buildSectionsPayload(pageSections.sections);

      formData.append("sections", JSON.stringify(sections));
      section_files.forEach((f) => formData.append("section_files", f));
      group_files.forEach((f) => formData.append("group_files", f));
      video_attachments.forEach((f) => formData.append("video_attachments", f));
      pageSections.videoCoverFiles.forEach((f) =>
        formData.append("video_covers", f),
      );

      const { data } = await api.put(ENDPOINTS.UPDATE_PAGE_CONTENT, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data.success) {
        toast.success(
          data.message || "Replay Sessions content updated successfully",
        );
        await loadContent(selectedCommunityId);
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating replay sessions content");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.appbarContainer}>
        <h2 className={styles.appbarTitle}>Edit Replay Sessions Content</h2>
      </div>

      <div className={styles.bodyContainer}>
        <div className={styles.rightSection}>
          <div className={styles.sectionsCard}>
            <div className={styles.rowInputGroup}>
              <label className={styles.inlineLabel}>Select Community *</label>
              <select
                className={styles.textInput}
                value={selectedCommunityId || ""}
                onChange={(e) => {
                  setSelectedCommunityId(
                    e.target.value ? Number(e.target.value) : null,
                  );
                  setAcademyTitle("");
                  pageSections.setSections([]);
                }}
                disabled={isSubmitting}
              >
                <option value="">-- Select a Community --</option>
                {communities.map((community) => (
                  <option key={community.id} value={community.id}>
                    {community.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedCommunityId && (
            <>
              <div className={styles.sectionsCard}>
                <div className={styles.rowInputGroup}>
                  <label className={styles.inlineLabel}>Page Title (Academy Title)</label>
                  <input
                    type="text"
                    value={academyTitle}
                    onChange={(e) => setAcademyTitle(e.target.value)}
                    className={styles.textInput}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <PageSectionsManager
                pageSections={pageSections}
                isSubmitting={isSubmitting}
              />

              <button
                className={`${styles.submitButton} ${isSubmitting ? styles.submitting : ""}`}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span>Updating...</span>
                    <span className={styles.submitIcon}>⟳</span>
                  </>
                ) : (
                  <>
                    <span>Update Content</span>
                    <span className={styles.submitIcon}>✓</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditReplaySessionsContentPage;
